<script setup>
// digitt: DENG-7203 — dedicated toggleable panel for the "user_information" dashboard app (rendered
// instead of a conversation tab). The contact phone travels via the DashboardAppFrame postMessage
// appContext, which the embedded page (progress frontapp-proxy) reads.
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import DashboardAppFrame from 'dashboard/components/widgets/DashboardApp/Frame.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';

const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const currentChat = useMapGetter('getSelectedChat');
const dashboardApps = useMapGetter('dashboardApps/getRecords');

const userInformationApp = computed(() =>
  dashboardApps.value.find(app => app.title === 'user_information')
);

const isOpen = computed(() => !!uiSettings.value?.is_user_information_open);

const isVisible = computed(
  () => isOpen.value && !!currentChat.value?.id && !!userInformationApp.value
);

const close = () => {
  updateUISettings({ is_user_information_open: false });
};

// digitt: DENG-7203 — resizable panel; width persisted in uiSettings
const MIN_WIDTH = 320;
const MAX_WIDTH = 640;
// digitt: DENG-7203 — default width matches the Contact sidebar (md:w-[320px])
const DEFAULT_WIDTH = 320;

const panelWidth = ref(
  uiSettings.value?.user_information_panel_width || DEFAULT_WIDTH
);
const isResizing = ref(false);
let startX = 0;
let startWidth = 0;

watch(
  () => uiSettings.value?.user_information_panel_width,
  width => {
    if (width && !isResizing.value) {
      panelWidth.value = width;
    }
  }
);

const clientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeMove = event => {
  if (!isResizing.value) return;
  if (event.cancelable) event.preventDefault();
  // panel sits on the right (LTR): dragging the left handle widens it
  const delta = startX - clientX(event);
  panelWidth.value = Math.min(
    Math.max(MIN_WIDTH, startWidth + delta),
    MAX_WIDTH
  );
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
  window.removeEventListener('mousemove', onResizeMove);
  window.removeEventListener('mouseup', onResizeEnd);
  window.removeEventListener('touchmove', onResizeMove);
  window.removeEventListener('touchend', onResizeEnd);
  updateUISettings({ user_information_panel_width: panelWidth.value });
};

const onResizeStart = event => {
  isResizing.value = true;
  startX = clientX(event);
  startWidth = panelWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  window.addEventListener('mousemove', onResizeMove);
  window.addEventListener('mouseup', onResizeEnd);
  window.addEventListener('touchmove', onResizeMove, { passive: false });
  window.addEventListener('touchend', onResizeEnd);
};

onMounted(() => {
  store.dispatch('dashboardApps/get');
});

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onResizeMove);
  window.removeEventListener('mouseup', onResizeEnd);
  window.removeEventListener('touchmove', onResizeMove);
  window.removeEventListener('touchend', onResizeEnd);
});
</script>

<template>
  <div
    v-if="isVisible"
    class="relative flex flex-col flex-shrink-0 h-full overflow-hidden bg-n-surface-2 border-n-weak ltr:border-l rtl:border-r"
    :style="{ width: `${panelWidth}px` }"
  >
    <!-- digitt: DENG-7203 — drag handle to resize the panel (matches the left-nav resize style) -->
    <div
      class="absolute top-0 z-40 w-1 h-full cursor-col-resize group ltr:left-0 rtl:right-0"
      @mousedown.prevent="onResizeStart"
      @touchstart.prevent="onResizeStart"
    >
      <div
        class="absolute top-0 w-px h-full transition-colors ltr:left-0 rtl:right-0 group-hover:bg-n-brand"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    <SidebarActionsHeader
      :title="t('CONVERSATION.HEADER.USER_INFORMATION')"
      @close="close"
    />
    <div class="flex flex-1 min-h-0">
      <DashboardAppFrame
        :key="`user-info-${currentChat.id}`"
        is-visible
        :position="999"
        :config="userInformationApp.content"
        :current-chat="currentChat"
      />
    </div>
  </div>
  <!-- digitt: DENG-7203 — overlay while resizing so the dashboard-app iframe doesn't swallow the
  mouse events mid-drag -->
  <Teleport to="body">
    <div v-if="isResizing" class="fixed inset-0 z-[9999] cursor-col-resize" />
  </Teleport>
</template>
